import { MUSIC_VIDEO_ERROR_CODES } from 'fym-shared/src/music24/musicVideoApi';
import { coverErrorCode, coverErrorStatus } from '../../coverEditor/doc/coverEditorErrors';

/** The `M24_*` code of an API error (`cause.code`, else `cause.reason`), or null. Pure. */
export const musicVideoErrorCode = coverErrorCode;
/** The HTTP status of an API error, or null. Pure. */
export const musicVideoErrorStatus = coverErrorStatus;

/** What a failed autosave means for the editor. */
export type CutSaveError =
  /** 409 `M24_CUT_STALE`: another save came first. The editor reads the cut again. */
  | 'stale'
  /** 409 `M24_CUT_WORKING`: the director writes the cut now. */
  | 'working'
  /** 400 `M24_CUT_DOC_INVALID`: the same save fails again. */
  | 'invalid'
  | 'failed';

/** Maps the refusal of `PATCH .../cuts/:cutUuid` (spec §4.4). Pure. */
export function cutSaveError(error: unknown): CutSaveError {
  const code = musicVideoErrorCode(error);
  if (code === MUSIC_VIDEO_ERROR_CODES.cutStale) {
    return 'stale';
  }
  if (code === MUSIC_VIDEO_ERROR_CODES.cutWorking) {
    return 'working';
  }
  if (code === MUSIC_VIDEO_ERROR_CODES.cutDocInvalid) {
    return 'invalid';
  }
  return 'failed';
}

/** What a failed "Make the video" means (frames e4, e5). */
export type MakeVideoError =
  /** 403 `M24_LIMIT_FEATURE`: full-song videos are on Pro. */
  | 'plan'
  /** 402 `M24_LIMIT_QUOTA`: not enough video credits. */
  | 'credits'
  /** 402 `M24_LIMIT_RELEASES`: no free release slot. */
  | 'releases'
  /** 400 `M24_FOOTAGE_GONE`: a clip of the cut was removed. */
  | 'footageGone'
  /** 400 `M24_FOOTAGE_PROCESSING`: an own clip of the cut has no render copy yet. */
  | 'footageProcessing'
  /** 400 `M24_NO_MASTER` / `M24_MASTER_PROCESSING`. */
  | 'noMaster'
  /** 403 `M24_DEMO_READ_ONLY`: the example artist. */
  | 'demo'
  | 'failed';

/** Maps the refusal of `POST .../cuts/:cutUuid/make`. Pure. */
export function makeVideoError(error: unknown): MakeVideoError {
  const code = musicVideoErrorCode(error);
  const status = musicVideoErrorStatus(error);
  if (code === MUSIC_VIDEO_ERROR_CODES.footageGone) {
    return 'footageGone';
  }
  if (code === MUSIC_VIDEO_ERROR_CODES.footageProcessing) {
    return 'footageProcessing';
  }
  if (code === 'M24_NO_MASTER' || code === 'M24_MASTER_PROCESSING') {
    return 'noMaster';
  }
  if (code === 'M24_DEMO_READ_ONLY') {
    return 'demo';
  }
  if (code === 'M24_LIMIT_RELEASES') {
    return 'releases';
  }
  if (code === 'M24_LIMIT_QUOTA' || status === 402) {
    return 'credits';
  }
  if (code === 'M24_LIMIT_FEATURE' || status === 403) {
    return 'plan';
  }
  return 'failed';
}

/** What a failed AI clip request means (frames h2, h4). */
export type AiClipError =
  /** 403 `M24_LIMIT_FEATURE`: AI clips are on Artist and up. */
  | 'plan'
  /** 402 `M24_LIMIT_QUOTA`: fewer than 4 credits. */
  | 'credits'
  /** 409 `M24_FOOTAGE_BUSY`: 3 clips are being made. */
  | 'busy'
  | 'demo'
  /** 429 `M24_AI_BUDGET_FREE_EXHAUSTED` and other rate limits. */
  | 'later'
  /** 400 `M24_REFERENCE_INVALID`: more than 3 references, or a picture that is not in the cast library. */
  | 'references'
  | 'failed';

/** Maps the refusal of `POST /footage/ai`. Pure. */
export function aiClipError(error: unknown): AiClipError {
  const code = musicVideoErrorCode(error);
  const status = musicVideoErrorStatus(error);
  if (code === MUSIC_VIDEO_ERROR_CODES.footageBusy || status === 409) {
    return 'busy';
  }
  if (code === 'M24_DEMO_READ_ONLY') {
    return 'demo';
  }
  if (code === 'M24_REFERENCE_INVALID') {
    return 'references';
  }
  if (status === 429) {
    return 'later';
  }
  if (code === 'M24_LIMIT_QUOTA' || status === 402) {
    return 'credits';
  }
  if (code === 'M24_LIMIT_FEATURE' || status === 403) {
    return 'plan';
  }
  return 'failed';
}

/** What a failed footage upload means (frame c2). `quota`: 402 / 409 `M24_FOOTAGE_QUOTA`, the storage is full. */
export type FootageUploadError = 'type' | 'tooLarge' | 'unreadable' | 'quota' | 'network' | 'failed';

/** Maps the refusal of the footage upload routes. Pure. */
export function footageUploadError(error: unknown): FootageUploadError {
  const code = musicVideoErrorCode(error);
  if (code === MUSIC_VIDEO_ERROR_CODES.footageQuota) {
    return 'quota';
  }
  if (code === MUSIC_VIDEO_ERROR_CODES.footageType) {
    return 'type';
  }
  if (code === MUSIC_VIDEO_ERROR_CODES.footageTooLarge) {
    return 'tooLarge';
  }
  if (code === MUSIC_VIDEO_ERROR_CODES.footageUnreadable) {
    return 'unreadable';
  }
  return 'failed';
}

/** Why a recut did not start. */
export type RecutError = 'limit' | 'busy' | 'failed';

/** Maps the refusal of `POST .../recut` (spec §4.4: 429 after 30 an hour, 409 while the director works). Pure. */
export function recutError(error: unknown): RecutError {
  const code = musicVideoErrorCode(error);
  const status = musicVideoErrorStatus(error);
  if (code === MUSIC_VIDEO_ERROR_CODES.cutRecutLimit || status === 429) {
    return 'limit';
  }
  if (code === MUSIC_VIDEO_ERROR_CODES.cutWorking || status === 409) {
    return 'busy';
  }
  return 'failed';
}

/** The storage numbers of a `M24_FOOTAGE_QUOTA` refusal (`cause.usedBytes`, `cause.limitBytes`). */
export interface FootageQuotaFacts {
  usedBytes: number;
  limitBytes: number;
}

/** The storage numbers of a `M24_FOOTAGE_QUOTA` refusal, or null (another error, or no numbers). Pure. */
export function footageQuotaFacts(error: unknown): FootageQuotaFacts | null {
  if (musicVideoErrorCode(error) !== MUSIC_VIDEO_ERROR_CODES.footageQuota) {
    return null;
  }
  const cause = (error as { m24Limit?: Record<string, unknown> }).m24Limit ?? {};
  const { usedBytes, limitBytes } = cause;
  return typeof usedBytes === 'number' && typeof limitBytes === 'number' ? { usedBytes, limitBytes } : null;
}

/** Why a new cut or a copy was not made. `limit`: 409 `M24_CUT_LIMIT`, the song holds the most cuts. */
export type CutCreateError = 'limit' | 'failed';

/** Maps the refusal of `POST .../cuts` (the first cut, "New cut", "Copy this cut"). Pure. */
export function cutCreateError(error: unknown): CutCreateError {
  return musicVideoErrorCode(error) === MUSIC_VIDEO_ERROR_CODES.cutLimit ? 'limit' : 'failed';
}
